import { useId, type InputHTMLAttributes } from "react";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

/**
 * 输入框 + 可选标签。
 *
 * **`<label>` 必须与 `<input>` 程序化关联**（`htmlFor` / `id`）：只有视觉上相邻的
 * 标签算不上可访问名，屏幕阅读器读不出这个框要填什么，`getByLabelText` 也找不着它。
 * `id` 用 `useId()` 生成（而不是写死或按 label 拼）：同一个页面上「摆放数量」这类
 * 标签会重复出现多次，写死的 id 会撞车、`htmlFor` 指向错误的框——比没有关联更糟。
 * 调用方显式传 `id` 时以调用方为准，便于外部已有 `<label htmlFor>` 的场景。
 */
export default function Input({ label, className = "", id, ...props }: Props) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-sm font-bold">
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={`min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black ${className}`}
        {...props}
      />
    </div>
  );
}
